<template>
    <div
        ref="topBarRoot"
        class="w-full text-primary-content"
        :class="colorClasses"
        role="region">
        <span class="sr-only">{{ $t('layout.topBar.headline') }}</span>
        <section class="m-auto flex flex-row justify-center gap-4 lg:gap-16 items-center relative px-6 max-w-screen-3xl overflow-hidden h-6">
            <client-only>
                <template v-for="(usp, index) in data" :key="index">
                    <div
                        v-if="hasContent(usp.title)"
                        :class="{ 'is-active' : index === activeUsp }"
                        class="usp-item text-xs leading-6 flex justify-center items-center gap-1"
                    >
                        <MiscIcon 
                            v-if="usp.showCheckIcon" 
                            name="check" 
                            size="sm" 
                            class="inline-flex"
                        />
                        {{ usp.title }}
                    </div>
                </template>
            </client-only>
        </section>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'

type HeaderConfig = NonNullable<Schemas['CmsElementA4gHeader']['config']>

const props = defineProps<{
    data: HeaderConfig['topBarUSPs']['value']
    color: HeaderConfig['topBarColor']['value']
}>()

const colorClassMap: Record<HeaderConfig['topBarColor']['value'], string> = {
    'primary': 'bg-primary',
    'sale': 'bg-sale',
    'secondary-content': 'bg-secondary-content'
}

const colorClasses = computed(() => {
    return colorClassMap[props.color] ?? 'bg-primary'
})

function hasContent (input: string | null) {
    return (input != null && input.trim() != '')
}

const activeUsp = ref(0)
let interval: ReturnType<typeof setInterval> | undefined

// Each tick starts a `usp-fade-in` animation, so only rotate while actually visible.
const topBarRoot = ref<HTMLElement | null>(null)

const mayRotate = useMayAnimate(topBarRoot)

watch(mayRotate, (may) => {
    if (interval != null) {
        clearInterval(interval)
        interval = undefined
    }

    if (may) interval = setInterval(setActiveUsp, 3000)
})

onUnmounted(() => {
    if (interval != null) clearInterval(interval)
})

function setActiveUsp () {
    const length = props.data.length

    if (activeUsp.value < length - 1) {
        activeUsp.value++
    } else {
        activeUsp.value = 0
    }
}
</script>
